import { useState, useEffect, useRef } from "react";
import LeftBar from "./LeftBar";
import ChatList from "./ChatList";
import ChatWindow from "./ChatWindow";
import StatusList from "./StatusList";
import CallsList from "./CallsList";
import SettingsList from "./SettingsList";
import CommunitiesList from "./CommunitiesList";
import AIChatWindow from "./AIChatWindow";
import { FaBars, FaMoon, FaSun, FaBullseye } from "react-icons/fa";
import "./Chat.css";

function MainLayout({ currentUser }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("chats");
  const sidebarRef = useRef(null);
  const [theme, setTheme] = useState("light");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedUser(null);
  };

  /* ===============================
     TAB RENDERING LOGIC
  =============================== */
  const renderSidebar = () => {
    switch (activeTab) {
      case "chats":
        return (
          <ChatList
            currentUser={currentUser}
            onSelectUser={handleSelectUser}
            selectedUser={selectedUser}
          />
        );
      case "status":
        return <StatusList currentUser={currentUser} />;
      case "calls":
        return <CallsList currentUser={currentUser} />;
      case "communities":
        return <CommunitiesList currentUser={currentUser} />;
      case "settings":
        return <SettingsList currentUser={currentUser} />;
      case "ai":
        return <AIChatWindow currentUser={currentUser} />;
      default:
        return null;
    }
  };

  /* ===============================
     AUTO THEME SYSTEM (Improved)
  =============================== */
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = (mode) => {
      setTheme(mode);
      document.documentElement.setAttribute("data-theme", mode);
    };

    if (savedTheme) {
      applyTheme(savedTheme);
    } else {
      applyTheme(mediaQuery.matches ? "dark" : "light");
    }

    // Listen to system changes
    const handleSystemChange = (e) => {
      if (!localStorage.getItem("theme")) {
        applyTheme(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleSystemChange);

    return () =>
      mediaQuery.removeEventListener("change", handleSystemChange);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";

    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };

  /* ===============================
     MOBILE SIDEBAR CONTROL
  =============================== */
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setMobileOpen(false);
  };

  const closeSidebar = () => {
    setMobileOpen(false);
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Click outside close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileOpen]);

  return (
    <div className="chat-page-wrapper">
      <div className="main-layout">
      {/* Overlay for mobile */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* LEFT NAVIGATION BAR */}
      <LeftBar 
        currentUser={currentUser} 
        theme={theme} 
        toggleTheme={toggleTheme} 
        activeTab={activeTab}
        setActiveTab={handleTabChange}
      />

      {/* SIDEBAR */}
      <div
        ref={sidebarRef}
        className={`sidebar ${mobileOpen ? "active" : ""} ${selectedUser ? "mobile-hidden" : ""}`}
      >
        {renderSidebar()}
      </div>

      {/* CHAT AREA */}
      <div className="chat-area">
        <div className="mobile-header">
          <FaBars
            className="menu-icon"
            onClick={() => setMobileOpen(!mobileOpen)}
          />
          <h3 className="app-title">Bharat Connect</h3>
        </div>

        <ChatWindow
          currentUser={currentUser}
          selectedUser={selectedUser}
          onBack={() => setSelectedUser(null)}
        />
      </div>
      </div>
    </div>
  );
}

export default MainLayout;
